import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ShowsDataService, ShowsColumnsService } from '../../services';
import { DialogShowFormComponent } from './show-form/show-form.component';
import { DialogShowDetailsComponent } from './show-details/show-details.component';
import { Show, ShowsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-shows',
  templateUrl: './shows.component.html',
  styleUrls: ['./shows.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ShowsComponent implements OnInit, OnDestroy {

  private showAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private showEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private showDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public showToDelete: Show = {};
  public shows: Show[] = [];
  public show: Show = {};
  public selectedShows: Show[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private showsDataService: ShowsDataService,
    private showsColumnsService: ShowsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ShowsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Shows - Management');
    this.primengConfig.ripple = true;
    this.getShows();
  }

  ngOnDestroy(): void {
    console.log('ShowsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getShows(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.showsDataService.getShows(this.parameters).subscribe((response: ShowsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.shows = response.results;
        } else {
          this.shows = this.shows.concat(response.results);
        }
        if (response.inlineCount > this.shows.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getShows();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SHOWSNOTLOADED')});
    });
  }

  importShows(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.showsDataService.addShows(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SHOWSCREATED')});
                if (proceed === j) {
                  this.getShows();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SHOWSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SHOWSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.showsDataService.updateShows(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('SHOWSUPDATED')});
                if (proceed === j) {
                  this.getShows();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SHOWSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('SHOWSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.showsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('shows.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedShows() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Shows?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.showsDataService.deleteShows(this.selectedShows).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Shows Deleted', life: 3000});
            this.getShows();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Shows not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Shows not deleted'});
        });
        this.selectedShows = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.shows, this.columns, 'Shows');
  }

  exportExcel() {
    ToolsService.exportExcel(this.shows, this.columns, 'Shows');
  }

  exportCsv() {
    ToolsService.exportCsv(this.shows, this.columns, 'Shows');
  }

  refresh() {
    this.showsDataService.clearCache();
    this.getShows();
  }

  showAdd(): void {
    if (!this.navigation.getParamValue('shows.addInDialog', true)) {
      this.router.navigate(['/shows/add']);
      return;
    }
    this.showAddDialog = this.dialogService.open(DialogShowFormComponent, {
      header: '', // this.translate.instant('NEWSHOW'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        show: {id: null},
        users: this.showsColumnsService.users,
      },
    });

    this.showAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Show added'});
        this.getShows();
      }
    });
  }

  showEdit(show: Show): void {
    if (!this.navigation.getParamValue('shows.editInDialog', true)) {
      this.router.navigate(['/shows/edit/' + show.id]);
      return;
    }
    this.showEditDialog = this.dialogService.open(DialogShowFormComponent, {
      header: '', // this.translate.instant('EDITSHOW') + ' ' + show.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        show: show,
        users: this.showsColumnsService.users,
      },
    });

    this.showEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Show updated'});
        this.getShows();
      }
    });
  }

  showDetails(show: Show): void {
    if (!this.navigation.getParamValue('shows.detailsInDialog', true)) {
      this.router.navigate(['/shows/details/' + show.id]);
      return;
    }
    this.showDetailsDialog = this.dialogService.open(DialogShowDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        show: show,
      }
    });
  }

  showDelete(show: Show): void {
    this.showToDelete = show;
    this.messageService.clear();
    this.messageService.add({
      key: 'showDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + show.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.showsDataService.deleteShow(this.showToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Show deleted'});
      this.getShows();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Show not deleted'});
    });
  }

  onDeleteReject(): void {
    this.showToDelete = {};
    this.messageService.clear('showDelete');
  }

}
